import { APP_INITIALIZER, Inject, Injectable } from '@angular/core';
import { BootstrapperBase } from '@trustedshops/tswp-core-common';
import { IdentityService, TOKENS } from '@trustedshops/tswp-core-authorization';
import { storeLogoutHandoffUrl } from '../../bootstrap-error/logout-handoff';

/**
 * Arms a best-effort capture of the Keycloak logout URL (incl. id_token_hint)
 * for the bootstrap-error page. Fire-and-forget: initialize() returns
 * immediately; the capture lands whenever an identity is established — even
 * after bootstrapModule() has already rejected and the error page is
 * showing (the error page reads the stash at click time, as a secondary
 * attempt alongside its own self-contained keycloak-js re-init). `identity`
 * never emits for an unauthenticated session or a failed session-provider
 * init (see IdentityServiceImpl.loadIdentityFrom), so no capture happens in
 * those cases.
 *
 * Independent of SystemCriticalBootstrapper and every other bootstrapper: it
 * only depends on the published IdentityService contract, so a failure
 * anywhere else in the initializer graph cannot prevent this from running.
 */
@Injectable()
export class LogoutHandoffBootstrapper implements BootstrapperBase {
  public constructor(
    @Inject(TOKENS.IdentityService)
    private readonly _identityService: IdentityService
  ) {}

  public async initialize(): Promise<void> {
    // Deliberately not awaited beyond registering the subscription.
    this._identityService.identity.subscribe((identity) => {
      if (!identity) {
        return; // not yet established, unauthenticated, or IdentityError
      }

      // keycloak-js createLogoutUrl():
      //   .../protocol/openid-connect/logout?client_id=...&post_logout_redirect_uri=<href>&id_token_hint=<idToken>
      // id_token_hint present -> Keycloak skips its confirmation screen.
      // No redirectUri arg = same call the user-menu logout makes in production.
      this._identityService.sessionProvider
        .getLogoutUri()
        .then(storeLogoutHandoffUrl)
        .catch(() => {
          // best-effort: the error page falls back to its own recovery attempts
        });
    });
  }
}

export const LogoutHandoffInitializer = [
  LogoutHandoffBootstrapper,
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (bootstrapper: LogoutHandoffBootstrapper) => () =>
      bootstrapper.initialize(),
    deps: [LogoutHandoffBootstrapper],
  },
];
